import type {ComponentType} from 'react';
import {ROUTES, type RouteName, type RoutePattern} from './routes';

import Q01ScanLanding from '../screens/Q01ScanLanding';
import Q02LanguageToggle from '../screens/Q02LanguageToggle';
import Q03MenuBrowse from '../screens/Q03MenuBrowse';
import Q04ItemDetail from '../screens/Q04ItemDetail';
import Q05Cart from '../screens/Q05Cart';
import Q07OrderSubmit from '../screens/Q07OrderSubmit';
import Q08PayFirstCheckout from '../screens/Q08PayFirstCheckout';
import Q09PaymentVerification from '../screens/Q09PaymentVerification';
import Q12QueueAheadLanding from '../screens/Q12QueueAheadLanding';
import Q14OrderStatus from '../screens/Q14OrderStatus';
import Q17OrderConfirmation from '../screens/Q17OrderConfirmation';
import Q19InvalidQr from '../screens/Q19InvalidQr';
import Q20VenueClosed from '../screens/Q20VenueClosed';
import BookWeb from '../screens/BookWeb';
import BookDone from '../screens/BookDone';
import BookManage from '../screens/BookManage';
import QrFeedback from '../screens/QrFeedback';

/**
 * The mounting point for every screen. One row per agent.
 *
 * ## Eager, not lazy
 *
 * Every screen is in the main bundle. Eleven small screens split into eleven
 * chunks would be eleven extra round trips on a 4G connection in a dining room,
 * and a guest crosses at least four of them (landing → menu → item → cart →
 * review) inside the first two minutes. One request that arrives is faster than
 * five that are small. Revisit only if a screen grows something genuinely heavy
 * — and if one does, split *that* screen, not all of them.
 *
 * ## The order matters
 *
 * Patterns are matched in the order below, most specific first. `/menu/:itemId`
 * must be tried before `/menu`, or an item deep link resolves to the browse
 * screen. (It cannot today — the matcher compares segment counts — but the
 * ordering is what makes that robust to a future optional segment.)
 */
export interface ScreenRoute {
  name: RouteName;
  pattern: RoutePattern;
  /** The screen agent's file. Nobody else edits it. */
  component: ComponentType;
  /** The spec number this route implements. */
  spec: string;
}

export const SCREEN_ROUTES: readonly ScreenRoute[] = [
  // `/pay/:orderId/return` before `/pay/:orderId`, for the reason stated above:
  // the matcher compares segment counts today, so the order is not what makes
  // this work — it is what keeps it working if a segment ever becomes optional.
  {
    name: 'paymentReturn',
    pattern: ROUTES.paymentReturn,
    component: Q09PaymentVerification,
    spec: 'Q9',
  },
  {name: 'pay', pattern: ROUTES.pay, component: Q08PayFirstCheckout, spec: 'Q8'},
  {name: 'item', pattern: ROUTES.item, component: Q04ItemDetail, spec: 'Q4'},
  {name: 'menu', pattern: ROUTES.menu, component: Q03MenuBrowse, spec: 'Q3'},
  {name: 'receipt', pattern: ROUTES.receipt, component: Q17OrderConfirmation, spec: 'Q17'},
  {name: 'scan', pattern: ROUTES.scan, component: Q01ScanLanding, spec: 'Q1'},
  {name: 'queue', pattern: ROUTES.queue, component: Q12QueueAheadLanding, spec: 'Q12'},
  {name: 'language', pattern: ROUTES.language, component: Q02LanguageToggle, spec: 'Q2'},
  {name: 'cart', pattern: ROUTES.cart, component: Q05Cart, spec: 'Q5'},
  {name: 'review', pattern: ROUTES.review, component: Q07OrderSubmit, spec: 'Q7'},
  {name: 'status', pattern: ROUTES.status, component: Q14OrderStatus, spec: 'Q14'},
  {name: 'feedback', pattern: ROUTES.feedback, component: QrFeedback, spec: 'Feedback'},
  {name: 'invalid', pattern: ROUTES.invalid, component: Q19InvalidQr, spec: 'Q19'},
  {name: 'closed', pattern: ROUTES.closed, component: Q20VenueClosed, spec: 'Q20'},
  // Booking web. The two `/book/<verb>/:token` patterns are three segments and
  // are listed before the one-segment `/book`, for the same reason the pay
  // routes are ordered: the matcher compares segment counts today, so the order
  // is what keeps this robust if a segment ever becomes optional.
  {name: 'bookDone', pattern: ROUTES.bookDone, component: BookDone, spec: 'BookDone'},
  {name: 'bookManage', pattern: ROUTES.bookManage, component: BookManage, spec: 'BookManage'},
  {name: 'book', pattern: ROUTES.book, component: BookWeb, spec: 'BookWeb'},
];

export const ROUTE_PATTERNS: readonly string[] = SCREEN_ROUTES.map((r) => r.pattern);

export function screenForPattern(pattern: string | null): ComponentType | null {
  if (!pattern) return null;
  return SCREEN_ROUTES.find((route) => route.pattern === pattern)?.component ?? null;
}
